import React, { useEffect, useId, useState } from "react";
import { shopsAPI, usersAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n";
import LanguageSelect from "./LanguageSelect";
import NewPinForm from "./NewPinForm";
import { buttonClass, inputClass } from "./AuthShell";

/**
 * Stands in front of the dashboard and the register while the signed-in
 * person still holds a credential they were handed — a default password, or
 * the default till PIN — and will not let them past until they have chosen
 * their own.
 *
 * Like the licence and suspension gates, this turns what the server knows into
 * something a person can act on; the server owns the flags (must_change_*).
 */
const CredentialGate = ({ children }) => {
  const { user, updateUser, logout } = useAuth();
  const { t } = useI18n();
  const [needsPin, setNeedsPin] = useState(false);
  const [checkedPin, setCheckedPin] = useState(false);

  const passwordDone = !user?.must_change_password;

  // Only asked once the password is settled, so the two steps come in order
  useEffect(() => {
    if (!user || !passwordDone || checkedPin) return;

    let cancelled = false;
    shopsAPI
      .current()
      .then(({ data }) => {
        if (!cancelled) setNeedsPin(Boolean(data.membership?.must_change_pin));
      })
      // No shop (or a suspended one) is dealt with elsewhere
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setCheckedPin(true);
      });

    return () => {
      cancelled = true;
    };
  }, [user, passwordDone, checkedPin]);

  if (!user) return children;

  if (!passwordDone) {
    return (
      <Frame title={t("auth.credentials.title")} onSignOut={logout}>
        <PasswordForm onDone={() => updateUser({ must_change_password: false })} />
      </Frame>
    );
  }

  if (needsPin) {
    return (
      <Frame title={t("auth.credentials.pinTitle")} onSignOut={logout} dark>
        <NewPinForm
          onSave={async (pin) => {
            try {
              await shopsAPI.changeMyPin(pin);
              setNeedsPin(false);
              return null;
            } catch (error) {
              return error.response?.data?.message || t("auth.credentials.pinFailed");
            }
          }}
        />
      </Frame>
    );
  }

  return children;
};

const Frame = ({ title, children, onSignOut, dark = false }) => {
  const { t } = useI18n();

  return (
    <div
      className={`fixed inset-0 z-[80] overflow-y-auto ${
        dark ? "bg-slate-950 text-slate-100" : "bg-gradient-to-br from-primary-500 to-primary-700"
      }`}
    >
      <div className="absolute top-4 end-4">
        <LanguageSelect tone={dark ? "dark" : "light"} />
      </div>

      <div className="min-h-full flex items-center justify-center p-4">
        <div
          className={`w-full max-w-md rounded-2xl p-8 ${
            dark ? "" : "bg-white shadow-2xl"
          }`}
        >
          <h1
            className={`text-2xl font-bold text-center mb-6 ${
              dark ? "text-white" : "text-gray-800"
            }`}
          >
            {title}
          </h1>
          {children}
          <button
            type="button"
            onClick={onSignOut}
            className={`mt-8 block mx-auto text-sm hover:underline ${
              dark ? "text-slate-400" : "text-gray-500"
            }`}
          >
            {t("auth.credentials.signOut")}
          </button>
        </div>
      </div>
    </div>
  );
};

const PasswordForm = ({ onDone }) => {
  const { t } = useI18n();
  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    setError("");

    if (password !== confirm) {
      setError(t("auth.credentials.mismatch"));
      return;
    }

    setBusy(true);
    try {
      await usersAPI.changePassword(current, password);
      onDone();
    } catch (err) {
      setError(
        err.response?.status === 401
          ? t("auth.credentials.wrongCurrent")
          : err.response?.data?.message || t("auth.credentials.failed"),
      );
      setBusy(false);
    }
  };

  // Each field's label is tied to its input, so a screen reader names it
  const fieldPrefix = useId();
  let fieldCount = 0;

  const field = (label, value, set, autoComplete, extra) => {
    const id = `${fieldPrefix}-${fieldCount++}`;

    return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <input
        id={id}
        type="password"
        value={value}
        onChange={(e) => set(e.target.value)}
        autoComplete={autoComplete}
        required
        className={inputClass}
        {...extra}
      />
    </div>
    );
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      <p className="text-sm text-gray-600">{t("auth.credentials.passwordIntro")}</p>

      {error && (
        <p className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {error}
        </p>
      )}

      {field(t("auth.credentials.current"), current, setCurrent, "current-password")}
      {field(t("auth.credentials.newPassword"), password, setPassword, "new-password", {
        minLength: 8,
      })}
      <p className="-mt-3 text-xs text-gray-500">{t("auth.credentials.hint")}</p>
      {field(t("auth.credentials.confirm"), confirm, setConfirm, "new-password")}

      <button type="submit" disabled={busy} className={buttonClass}>
        {busy ? t("auth.credentials.saving") : t("auth.credentials.save")}
      </button>
    </form>
  );
};

export default CredentialGate;
